import { useEffect, useState, useCallback } from 'react';
import './Legal.scss';
import TopNav from '../../components/TopNav/TopNav';
import SideBar from '../../components/SideBar/SideBar';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Save, RefreshCw } from 'lucide-react';

// Paste-first legal editor. One big textarea per doc. Paste your full
// document, click Save, and the parser writes the structured form to
// the backend. Mobile app + website pick up the new content on next
// fetch (single source of truth).

const TABS = [
    { type: 'app',             document: 'policy',   label: 'App · Privacy' },
    { type: 'app',             document: 'terms',    label: 'App · Terms' },
    { type: 'business',        document: 'policy',   label: 'Business · Privacy' },
    { type: 'business',        document: 'terms',    label: 'Business · Terms' },
    { type: 'zonavip',         document: 'policy',   label: 'VIP · Privacy' },
    { type: 'zonavip',         document: 'terms',    label: 'VIP · Terms' },
    { type: 'brandambassador', document: 'policy',   label: 'Ambassador · Privacy' },
    { type: 'brandambassador', document: 'contract', label: 'Ambassador · Contract' },
];

// Stringify the structured doc back to a paste-friendly text format.
// "Effective Date: ..." line, then intro, then numbered sections with
// a blank line between each.
function stringify(doc) {
    if (!doc) return '';
    const parts = [];
    if (doc.effectiveDate) parts.push(`Effective Date: ${doc.effectiveDate}`);
    if (doc.intro) parts.push(doc.intro);
    (doc.sections || []).forEach((s) => {
        parts.push(`${s.number || ''}. ${s.title || ''}\n${s.body || ''}`);
    });
    return parts.join('\n\n');
}

// Parse a pasted document into the structured shape the backend stores.
function parse(text) {
    const stripBold = (s) => s.replace(/^\*\*+/, '').replace(/\*\*+$/, '').trim();
    const stripMarkdown = (s) =>
        String(s)
            .replace(/\*\*+([^*\n]+)\*\*+/g, '$1')
            .replace(/__([^_\n]+)__/g, '$1')
            .replace(/\*\*+/g, '')
            .replace(/\\([_*])/g, '$1');
    const lines = String(text || '').split(/\r?\n/);

    let effectiveDate = '';
    let intro = '';
    let docTitleSeen = false;
    const sections = [];
    let current = null;

    const sectionRe = /^\s*(?:\*\*)?(\d{1,3})[\.\)]\s+(.+?)\s*$/;
    const titleHeaderRe = /^(PRIVACY POLICY|TERMS AND CONDITIONS|TERMS & CONDITIONS|TERMS OF SERVICE|CONTENT POLICY)\b.*$/i;
    const effectiveRe = /^[\*\s_>#-]*Effective Date:?\s*(.+?)\s*\**\s*$/i;

    for (const raw of lines) {
        const trimmed = raw.trim();
        const effMatch = trimmed.match(effectiveRe);
        if (effMatch && !effectiveDate) {
            effectiveDate = stripBold(effMatch[1]).replace(/^\*+|\*+$/g, '').trim();
            continue;
        }
        if (!docTitleSeen && titleHeaderRe.test(trimmed)) {
            docTitleSeen = true;
            continue;
        }
        const secMatch = trimmed.match(sectionRe);
        if (secMatch) {
            if (current) sections.push({ ...current, body: stripMarkdown(current.body).trim() });
            current = {
                number: secMatch[1],
                title: stripBold(secMatch[2]).replace(/[:\.]\s*$/, ''),
                body: '',
            };
            continue;
        }
        if (current) {
            current.body += (current.body ? '\n' : '') + raw;
        } else {
            intro += (intro ? '\n' : '') + raw;
        }
    }
    if (current) sections.push({ ...current, body: stripMarkdown(current.body).trim() });

    const renumbered = sections.map((s, i) => ({ ...s, number: String(i + 1) }));
    return {
        effectiveDate,
        intro: stripMarkdown(intro).trim(),
        sections: renumbered,
    };
}

export default function Legal() {
    const [activeIdx, setActiveIdx] = useState(0);
    const active = TABS[activeIdx];
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/legal/${active.type}/${active.document}`);
            setText(stringify(data));
        } catch (err) {
            if (err?.response?.status === 404) {
                setText('');
            } else {
                console.error(err);
                toast.error('Failed to load document');
            }
        } finally {
            setLoading(false);
        }
    }, [active.type, active.document]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const parsed = parse(text);
            if (!parsed.sections.length) {
                toast.error('No numbered sections detected. Use "1. Title" format for each section.');
                setSaving(false);
                return;
            }
            await api.put(`/legal/${active.type}/${active.document}`, parsed);
            toast.success(`${active.label} saved — live on website + apps`);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.error || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <main>
            <TopNav />
            <div className='main-container'>
                <SideBar />
                <article className="page-content">
                    <section className="legal-hub">
                        <div className="legal-hub__header">
                            <span className="legal-hub__eyebrow">Settings · Legal</span>
                            <h1>Legal Documents</h1>
                            <p>Paste the full document for the surface you're updating. The parser detects the Effective Date line, intro paragraphs, and numbered sections (1., 2., 3.). Saves immediately to the website and the corresponding app surface.</p>
                        </div>

                        <div className="legal-hub__tabs" role="tablist">
                            {TABS.map((t, i) => (
                                <button
                                    key={t.label}
                                    role="tab"
                                    aria-selected={i === activeIdx}
                                    className={`legal-hub__tab ${i === activeIdx ? 'is-active' : ''}`}
                                    onClick={() => setActiveIdx(i)}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="legal-hub__editor">
                            <div className="legal-hub__toolbar">
                                <button className="legal-hub__btn" onClick={load} disabled={loading || saving} title="Reload from server">
                                    <RefreshCw size={14} /> Reload
                                </button>
                                <button className="legal-hub__btn legal-hub__btn--primary" onClick={save} disabled={loading || saving || !text.trim()}>
                                    <Save size={14} /> {saving ? 'Saving…' : 'Save & Publish'}
                                </button>
                            </div>
                            <textarea
                                className="legal-hub__textarea"
                                placeholder={`Paste the full ${active.label} here.\n\nExample format:\n\nEffective Date: May 11, 2026\n\nIntro paragraph...\n\n1. Section Title\nSection body...\n\n2. Another Section\n...`}
                                value={loading ? 'Loading…' : text}
                                onChange={(e) => setText(e.target.value)}
                                disabled={loading || saving}
                            />
                        </div>
                    </section>
                </article>
            </div>
        </main>
    );
}
